import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  UserGroupIcon, 
  ClockIcon,
  ArrowRightIcon,
  TvIcon
} from '@heroicons/react/24/outline';
import { apiService, Channel, Show, Episode } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Breadcrumb from '../components/Breadcrumb';
import { useAudio } from '../contexts/AudioContext';

const ChannelDetail: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const { startSession } = useAudio();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!channelId) return;
      
      try {
        const [channelData, showsData, episodesData] = await Promise.all([
          apiService.getChannel(channelId),
          apiService.getShows(),
          apiService.getEpisodes()
        ]);
        
        setChannel(channelData);
        setShows(showsData.filter(show => show.channelId === channelId));
        setEpisodes(episodesData);
      } catch (error) {
        console.error('Error fetching channel data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [channelId]);

  const handleStartSession = async (episodeId: string, episodeTitle: string) => {
    try {
      setStartingSession(episodeId);
      await startSession(episodeId, episodeTitle);
      // Audio will start playing automatically via the floating player
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  const getLatestEpisodeForShow = (showId: string) => {
    const showEpisodes = episodes.filter(episode => episode.showId === showId);
    return showEpisodes.sort((a, b) => 
      new Date(b.publishedAt || b.createdAt).getTime() - 
      new Date(a.publishedAt || a.createdAt).getTime()
    )[0];
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!channel) {
    return (
      <div className="text-center py-12">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Channel Not Found
        </h3>
        <button
          onClick={() => navigate('/channels')}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300"
        >
          <ArrowRightIcon className="w-5 h-5" />
          <span>Back to Channels</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: 'Channels', href: '/channels' },
          { label: channel.name, isCurrent: true }
        ]} 
      />

      {/* Channel Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            {channel.logoUrl ? (
              <img 
                src={channel.logoUrl} 
                alt={channel.name}
                className="w-20 h-20 rounded-lg object-cover"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <TvIcon className="w-10 h-10 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {channel.name}
              </h1>
              {channel.description && (
                <p className="text-lg text-gray-600 dark:text-gray-400 mt-2 max-w-2xl">
                  {channel.description}
                </p>
              )}
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${
            channel.status === 'active' 
              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}>
            {channel.status}
          </span>
        </div>
        
        {channel.category && (
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
              {channel.category}
            </span>
            {channel.tags.length > 0 && channel.tags.slice(0, 5).map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Shows in this Channel */}
      {shows.length > 0 ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Shows in {channel.name}
            </h2>
            <span className="text-gray-600 dark:text-gray-400">
              {shows.length} show{shows.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shows.map((show) => {
              const latestEpisode = getLatestEpisodeForShow(show._id);
              const episodeCount = episodes.filter(episode => episode.showId === show._id).length;
              
              return (
                <div
                  key={show._id}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => navigate(`/channels/${channelId}/shows/${show._id}`)}
                >
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {show.name}
                    </h3>
                    {show.description && (
                      <p className="text-gray-600 dark:text-gray-400 line-clamp-3">
                        {show.description}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      <UserGroupIcon className="w-4 h-4" />
                      <span>{show.panelistIds.length} panelist{show.panelistIds.length !== 1 ? 's' : ''}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      <ClockIcon className="w-4 h-4" />
                      <span>{show.frequency}</span>
                    </div>

                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      <TvIcon className="w-4 h-4" />
                      <span>{episodeCount} episode{episodeCount !== 1 ? 's' : ''}</span>
                    </div>

                    {show.category && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                        {show.category}
                      </span>
                    )}
                  </div>

                  {/* Latest Episode Preview */}
                  {latestEpisode && (
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mb-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Latest Episode:</p>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                            {latestEpisode.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(latestEpisode.publishedAt || latestEpisode.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartSession(latestEpisode._id, latestEpisode.title);
                          }}
                          disabled={startingSession === latestEpisode._id}
                          className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm font-semibold flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {startingSession === latestEpisode._id ? (
                            <>
                              <div className="w-4 h-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
                              <span>Starting...</span>
                            </>
                          ) : (
                            <>
                              <PlayIcon className="w-4 h-4" />
                              <span>Play</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* View Episodes Button */}
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/channels/${channelId}/shows/${show._id}`);
                      }}
                      className="w-full text-center text-sm text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 font-medium flex items-center justify-center space-x-1 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span>View All Episodes</span>
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <TvIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Shows Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            This channel doesn't have any shows yet. Check back soon!
          </p>
        </div>
      )}
    </div>
  );
};

export default ChannelDetail;
